import { getToken, clearToken } from '../utils/authToken';
/**
 * Panel de Administración de Términos (VTC Module)
 * Permite a los administradores:
 * - Ver la versión actual de términos
 * - Incrementar la versión (forzar re-aceptación global)
 * - Editar el texto de los términos
 * - Ver estadísticas de aceptación por versión
 * - Ver logs de auditoría de aceptación
 */

import { useState, useEffect, useCallback } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import {
  FileText, Shield, AlertTriangle, CheckCircle, Loader2, X,
  Plus, Users, History, Edit3, Save, RefreshCw, ChevronRight,
  BarChart3, Clock, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


const AdminTermsManagement = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  
  // Estados principales
  const [isLoading, setIsLoading] = useState(true);
  const [termsInfo, setTermsInfo] = useState(null);
  const [termsText, setTermsText] = useState('');
  const [isEditingText, setIsEditingText] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [confirmIncrement, setConfirmIncrement] = useState(false);

  // Textos i18n
  const txt = {
    es: {
      title: 'Gestión de Términos y Condiciones',
      subtitle: 'Control de versiones y re-aceptación obligatoria',
      currentVersion: 'Versión Actual',
      lastUpdated: 'Última actualización',
      usersUpToDate: 'Usuarios al día',
      usersNeedUpdate: 'Necesitan actualizar',
      totalUsers: 'Total usuarios',
      versionBreakdown: 'Usuarios por Versión',
      incrementVersion: 'Incrementar Versión',
      incrementWarning: 'Al incrementar la versión, TODOS los usuarios deberán aceptar los nuevos términos en su próximo login.',
      confirmIncrement: '¿Estás seguro de incrementar la versión?',
      editText: 'Editar Texto',
      saveText: 'Guardar Texto',
      viewLogs: 'Ver Logs de Auditoría',
      logsTitle: 'Historial de Aceptaciones',
      noLogs: 'Sin registros de aceptación',
      refresh: 'Actualizar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      loading: 'Cargando...',
      saving: 'Guardando...',
      customText: 'Texto personalizado activo',
      defaultText: 'Usando texto predeterminado',
      affectedUsers: 'usuarios afectados'
    },
    en: {
      title: 'Terms and Conditions Management',
      subtitle: 'Version control and mandatory re-acceptance',
      currentVersion: 'Current Version',
      lastUpdated: 'Last updated',
      usersUpToDate: 'Users up to date',
      usersNeedUpdate: 'Need to update',
      totalUsers: 'Total users',
      versionBreakdown: 'Users by Version',
      incrementVersion: 'Increment Version',
      incrementWarning: 'By incrementing the version, ALL users will need to accept the new terms on their next login.',
      confirmIncrement: 'Are you sure you want to increment the version?',
      editText: 'Edit Text',
      saveText: 'Save Text',
      viewLogs: 'View Audit Logs',
      logsTitle: 'Acceptance History',
      noLogs: 'No acceptance records',
      refresh: 'Refresh',
      cancel: 'Cancel',
      confirm: 'Confirm',
      loading: 'Loading...',
      saving: 'Saving...',
      customText: 'Custom text active',
      defaultText: 'Using default text',
      affectedUsers: 'affected users'
    }
  };
  
  const t = txt[language] || txt.es;
  
  // CUADRO NEGRO FIX feb-2026: La declaración local `const getToken = () => getToken();`
  // creaba recursión infinita y sombreaba el import real de '../utils/authToken'.
  // Causa raíz del bug "Error al cargar información de términos".
  // Ahora usamos el import directo, sin override.

  // Cargar información de términos
  const loadTermsInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only via axios.defaults
      
      const [infoRes, textRes] = await Promise.all([
        axios.get(`${API}/admin/terms/current`, { headers }),
        axios.get(`${API}/admin/terms/text`, { headers })
      ]);
      
      setTermsInfo(infoRes.data);
      if (textRes.data.has_custom_text) {
        setTermsText(textRes.data.text || '');
      }
    } catch (error) {
      console.error('Error loading terms info:', error);
      toast.error(language === 'es' ? 'Error al cargar información de términos' : 'Error loading terms information');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar logs de auditoría
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/terms/acceptance-logs`, {
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error(language === 'es' ? 'Error al cargar logs' : 'Error loading logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Incrementar versión
  const handleIncrementVersion = async () => {
    setIsSaving(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/admin/terms/increment-version`,
        { increment: true },
        {}
      );
      
      toast.success(language === 'es' ? `Versión actualizada a v${response.data.new_version}` : `Version updated to v${response.data.new_version}`);
      setConfirmIncrement(false);
      loadTermsInfo();
    } catch (error) {
      console.error('Error incrementing version:', error);
      toast.error(language === 'es' ? 'Error al incrementar versión' : 'Error incrementing version');
    } finally {
      setIsSaving(false);
    }
  };

  // Guardar texto de términos
  const handleSaveText = async () => {
    if (!termsText.trim()) {
      toast.error(language === 'es' ? 'El texto no puede estar vacío' : 'Text cannot be empty');
      return;
    }
    
    setIsSaving(true);
    try {
      const token = getToken();
      await axios.put(
        `${API}/admin/terms/text`,
        { new_text: termsText },
        {}
      );
      
      toast.success(language === 'es' ? 'Texto de términos guardado' : 'Terms text saved');
      setIsEditingText(false);
      loadTermsInfo();
    } catch (error) {
      console.error('Error saving text:', error);
      toast.error(language === 'es' ? 'Error al guardar texto' : 'Error saving text');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTermsInfo();
    }
  }, [isOpen, loadTermsInfo]);

  useEffect(() => {
    if (showLogs) {
      loadLogs();
    }
  }, [showLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const modalContent = (
    <div 
      data-testid="admin-terms-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-zinc-800/50">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">{t.title}</h2>
              <p className="text-sm text-zinc-400">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
              <span className="ml-3 text-zinc-400">{t.loading}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Current Version */}
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-indigo-400 mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.currentVersion}</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    v{termsInfo?.current_version || 1}
                  </div>
                  {termsInfo?.last_updated && (
                    <div className="text-xs text-zinc-400 mt-1">
                      {t.lastUpdated}: {new Date(termsInfo.last_updated).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}
                    </div>
                  )}
                </div>

                {/* Users Up to Date */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <UserCheck className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.usersUpToDate}</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {termsInfo?.users_up_to_date || 0}
                  </div>
                </div>

                {/* Users Need Update */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.usersNeedUpdate}</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {termsInfo?.users_needing_update || 0}
                  </div>
                </div>

                {/* Total Users */}
                <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-zinc-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.totalUsers}</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {termsInfo?.total_users || 0}
                  </div>
                </div>
              </div>

              {/* Version Breakdown */}
              {termsInfo?.users_by_version && Object.keys(termsInfo.users_by_version).length > 0 && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-zinc-300 mb-4">
                    <BarChart3 className="w-4 h-4" />
                    <span className="font-medium">{t.versionBreakdown}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(termsInfo.users_by_version)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .map(([version, count]) => (
                        <div 
                          key={version}
                          className={`px-3 py-2 rounded-lg ${
                            version === `v${termsInfo.current_version}`
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                              : 'bg-zinc-700/50 text-zinc-300'
                          }`}
                        >
                          <span className="font-medium">{version}</span>
                          <span className="ml-2 text-sm opacity-80">{count} usuarios</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {/* Increment Version */}
                {!confirmIncrement ? (
                  <button
                    data-testid="increment-version-btn"
                    onClick={() => setConfirmIncrement(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t.incrementVersion}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-400 font-medium">{t.confirmIncrement}</p>
                      <p className="text-xs text-red-300/70">{termsInfo?.users_needing_update || termsInfo?.total_users || 0} {t.affectedUsers}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConfirmIncrement(false)}
                        className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white"
                      >
                        {t.cancel}
                      </button>
                      <button
                        data-testid="confirm-increment-btn"
                        onClick={handleIncrementVersion}
                        disabled={isSaving}
                        className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.confirm}
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Text */}
                <button
                  data-testid="edit-terms-text-btn"
                  onClick={() => setIsEditingText(!isEditingText)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 rounded-lg transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  {t.editText}
                </button>

                {/* View Logs */}
                <button
                  data-testid="view-logs-btn"
                  onClick={() => setShowLogs(!showLogs)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                >
                  <History className="w-4 h-4" />
                  {t.viewLogs}
                </button>

                {/* Refresh */}
                <button
                  onClick={loadTermsInfo}
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t.refresh}
                </button>
              </div>

              {/* Text Editor */}
              {isEditingText && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-zinc-300">
                      {termsInfo?.has_custom_text ? t.customText : t.defaultText}
                    </span>
                    <button
                      data-testid="save-terms-text-btn"
                      onClick={handleSaveText}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {t.saveText}
                    </button>
                  </div>
                  <textarea
                    data-testid="terms-text-editor"
                    value={termsText}
                    onChange={(e) => setTermsText(e.target.value)}
                    className="w-full h-64 bg-zinc-900 border border-zinc-600 rounded-lg p-4 text-sm text-zinc-300 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ingresa el texto personalizado de los términos..."
                  />
                </div>
              )}

              {/* Logs */}
              {showLogs && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t.logsTitle}
                  </h3>
                  {logsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
                    </div>
                  ) : logs.length === 0 ? (
                    <p className="text-center text-zinc-500 py-8">{t.noLogs}</p>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {logs.map((log, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <div>
                              <span className="text-sm text-white">{log.user_email}</span>
                              <span className="mx-2 text-zinc-600">•</span>
                              <span className="text-sm text-indigo-400">v{log.terms_version}</span>
                            </div>
                          </div>
                          <span className="text-xs text-zinc-500">
                            {new Date(log.accepted_at).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Warning Info */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-300">
                    <p className="font-medium mb-1">Información Importante</p>
                    <p className="text-amber-300/80">
                      {t.incrementWarning}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AdminTermsManagement;
